'use client';
import React, { useState, useMemo, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';
import StatusBadge from '@/components/ui/StatusBadge';
import Modal from '@/components/ui/Modal';
import EmptyState from '@/components/ui/EmptyState';
import { LeadCardSkeleton } from '@/components/ui/LoadingSkeleton';
import { toast } from 'sonner';
import { createClient } from '@/lib/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

type LeadStage = 'New' | 'Contacted' | 'Survey' | 'Booking' | 'Closing';

interface Lead {
  id: string;
  name: string;
  phone: string;
  kavling_name: string;
  project_name: string;
  stage: LeadStage;
  source: string;
  last_contact_at: string | null;
  next_follow_up_at: string | null;
  next_action: string | null;
  notes: string | null;
  budget: string | null;
  agent_id: string | null;
  agent_code: string | null;
  created_at: string;
  timeline?: { id: string; created_at: string; action: string; note: string | null }[];
}

const stages: LeadStage[] = ['New', 'Contacted', 'Survey', 'Booking', 'Closing'];
const stageColors: Record<LeadStage, string> = {
  New: 'border-t-purple',
  Contacted: 'border-t-blue',
  Survey: 'border-t-orange',
  Booking: 'border-t-secondary',
  Closing: 'border-t-primary',
};
const stageBg: Record<LeadStage, string> = {
  New: 'bg-purple/5',
  Contacted: 'bg-blue/5',
  Survey: 'bg-orange/5',
  Booking: 'bg-secondary/5',
  Closing: 'bg-primary/5',
};

const sourceOptions = ['Semua Sumber', 'WhatsApp', 'Referral', 'Website', 'Instagram'];

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('id-ID', { day: '2-digit', month: 'short', year: 'numeric' });
}

export default function AgentCRMContent() {
  const { user, profile } = useAuth();
  const [leads, setLeads] = useState<Lead[]>([]);
  const [viewMode, setViewMode] = useState<'kanban' | 'list'>('kanban');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedSource, setSelectedSource] = useState('Semua Sumber');
  const [selectedLead, setSelectedLead] = useState<Lead | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [newNote, setNewNote] = useState('');
  const [isSavingNote, setIsSavingNote] = useState(false);

  const supabase = createClient();

  // Fetch leads from Supabase
  useEffect(() => {
    if (!user) return;

    const fetchLeads = async () => {
      setIsLoading(true);
      const { data, error } = await supabase
        .from('leads')
        .select('*, timeline:lead_timeline(*)')
        .order('created_at', { ascending: false });

      if (!error && data) {
        setLeads(data as Lead[]);
      } else if (error) {
        console.error('Fetch leads error:', error.message);
      }
      setIsLoading(false);
    };

    fetchLeads();

    // Real-time subscription for new leads
    const channel = supabase
      .channel('agent_leads_realtime')
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'leads' },
        async (payload) => {
          // Fetch full lead with timeline
          const { data } = await supabase
            .from('leads')
            .select('*, timeline:lead_timeline(*)')
            .eq('id', payload.new.id)
            .single();
          if (data) {
            setLeads((prev) => [data as Lead, ...prev]);
            toast.success(`Lead baru masuk: ${(payload.new as any).name}`);
          }
        }
      )
      .on(
        'postgres_changes',
        { event: 'UPDATE', schema: 'public', table: 'leads' },
        (payload) => {
          setLeads((prev) =>
            prev.map((l) => l.id === payload.new.id ? { ...l, ...(payload.new as Lead) } : l)
          );
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user]);

  const filteredLeads = useMemo(() => {
    return leads.filter((l) => {
      if (searchQuery && !l.name.toLowerCase().includes(searchQuery.toLowerCase()) &&
        !(l.kavling_name || '').toLowerCase().includes(searchQuery.toLowerCase())) return false;
      if (selectedSource !== 'Semua Sumber' && l.source !== selectedSource) return false;
      return true;
    });
  }, [leads, searchQuery, selectedSource]);

  const getLeadsByStage = (stage: LeadStage) => filteredLeads.filter((l) => l.stage === stage);

  const updateLeadStatus = async (leadId: string, newStatus: LeadStage) => {
    const { error } = await supabase
      .from('leads')
      .update({ stage: newStatus, updated_at: new Date().toISOString() })
      .eq('id', leadId);

    if (error) {
      toast.error('Gagal memperbarui status lead');
      return;
    }

    setLeads((prev) => prev.map((l) => l.id === leadId ? { ...l, stage: newStatus } : l));
    toast.success(`Lead dipindah ke ${newStatus}`);
  };

  const addNote = async (leadId: string) => {
    if (!newNote.trim()) return;
    setIsSavingNote(true);

    const { data: timelineEntry, error } = await supabase
      .from('lead_timeline')
      .insert({
        lead_id: leadId,
        action: 'Catatan',
        note: newNote,
        created_by: user?.id || null,
      })
      .select()
      .single();

    if (error) {
      toast.error('Gagal menyimpan catatan');
      setIsSavingNote(false);
      return;
    }

    setLeads((prev) => prev.map((l) => {
      if (l.id !== leadId) return l;
      return {
        ...l,
        timeline: [...(l.timeline || []), timelineEntry],
      };
    }));

    // Update selected lead if open
    if (selectedLead?.id === leadId) {
      setSelectedLead((prev) => prev ? {
        ...prev,
        timeline: [...(prev.timeline || []), timelineEntry],
      } : prev);
    }

    setNewNote('');
    toast.success('Catatan berhasil ditambahkan');
    setIsSavingNote(false);
  };

  return (
    <div className="p-4 lg:p-6 xl:p-8 max-w-screen-2xl mx-auto">
      {/* Header controls */}
      <div className="flex flex-col sm:flex-row gap-3 mb-5">
        <div className="relative flex-1">
          <Icon name="MagnifyingGlassIcon" size={17} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input
            type="text"
            placeholder="Cari lead berdasarkan nama atau properti..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-9 pr-4 py-2.5 bg-card border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
          />
        </div>
        <div className="flex items-center gap-2">
          <select
            value={selectedSource}
            onChange={(e) => setSelectedSource(e.target.value)}
            className="px-3 py-2.5 bg-card border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
          >
            {sourceOptions.map((s) => <option key={`src-${s}`} value={s}>{s}</option>)}
          </select>
          <div className="flex items-center border border-border rounded-lg overflow-hidden bg-card">
            <button
              onClick={() => setViewMode('kanban')}
              className={`p-2.5 transition-colors ${viewMode === 'kanban' ? 'bg-primary text-white' : 'text-muted-foreground hover:bg-muted'}`}
              aria-label="Kanban view"
            >
              <Icon name="ViewColumnsIcon" size={18} />
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`p-2.5 transition-colors ${viewMode === 'list' ? 'bg-primary text-white' : 'text-muted-foreground hover:bg-muted'}`}
              aria-label="List view"
            >
              <Icon name="ListBulletIcon" size={18} />
            </button>
          </div>
        </div>
      </div>

      {/* Pipeline summary */}
      <div className="flex gap-2 overflow-x-auto scrollbar-hide pb-1 mb-5">
        {stages.map((stage) => {
          const count = getLeadsByStage(stage).length;
          return (
            <div key={`summary-${stage}`} className="flex-shrink-0 flex items-center gap-2 px-3 py-2 bg-card border border-border rounded-lg">
              <StatusBadge status={stage} size="sm" />
              <span className="text-sm font-bold text-foreground font-tabular">{count}</span>
            </div>
          );
        })}
        <div className="flex-shrink-0 flex items-center gap-2 px-3 py-2 bg-primary/5 border border-primary/20 rounded-lg">
          <span className="text-xs font-semibold text-primary">Total</span>
          <span className="text-sm font-bold text-primary font-tabular">{filteredLeads.length}</span>
        </div>
      </div>

      {/* Loading state */}
      {isLoading && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={`crm-skel-${i}`} className="flex flex-col gap-3">
              <LeadCardSkeleton />
              <LeadCardSkeleton />
            </div>
          ))}
        </div>
      )}

      {/* Empty state */}
      {!isLoading && filteredLeads.length === 0 && (
        <EmptyState
          icon="UserGroupIcon"
          title="Belum ada lead"
          description="Lead dari form website akan muncul di sini secara otomatis"
        />
      )}

      {/* Kanban view */}
      {!isLoading && filteredLeads.length > 0 && viewMode === 'kanban' && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 overflow-x-auto">
          {stages.map((stage) => {
            const stageLeads = getLeadsByStage(stage);
            return (
              <div key={`col-${stage}`} className={`flex flex-col gap-3 min-w-[200px] ${stageBg[stage]} rounded-xl p-3 border-t-4 ${stageColors[stage]}`}>
                <div className="flex items-center justify-between mb-1">
                  <StatusBadge status={stage} size="sm" />
                  <span className="text-xs font-bold text-muted-foreground">{stageLeads.length}</span>
                </div>
                {stageLeads.map((lead) => (
                  <div
                    key={lead.id}
                    onClick={() => setSelectedLead(lead)}
                    className="bg-card border border-border rounded-xl p-3 cursor-pointer hover:shadow-sm hover:border-primary/30 transition-all"
                  >
                    <p className="text-sm font-semibold text-foreground mb-0.5">{lead.name}</p>
                    <p className="text-xs text-muted-foreground truncate">{lead.kavling_name}</p>
                    <p className="text-xs text-muted-foreground">{lead.project_name}</p>
                    <div className="flex items-center justify-between mt-2">
                      <span className="text-xs text-muted-foreground">{lead.source}</span>
                      <span className="text-xs text-muted-foreground">{formatDate(lead.created_at)}</span>
                    </div>
                    {lead.budget && (
                      <p className="text-xs font-semibold text-primary mt-1">{lead.budget}</p>
                    )}
                  </div>
                ))}
              </div>
            );
          })}
        </div>
      )}

      {/* List view */}
      {!isLoading && filteredLeads.length > 0 && viewMode === 'list' && (
        <div className="bg-card border border-border rounded-xl overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="border-b border-border bg-background">
                {['Nama', 'Properti', 'Status', 'Sumber', 'Budget', 'Tanggal', 'Aksi'].map((h) => (
                  <th key={`th-${h}`} className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wide whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filteredLeads.map((lead) => (
                <tr key={lead.id} className="border-b border-border hover:bg-muted/50 transition-colors">
                  <td className="px-4 py-3">
                    <div>
                      <p className="text-sm font-semibold text-foreground">{lead.name}</p>
                      <p className="text-xs text-muted-foreground">{lead.phone}</p>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <p className="text-sm text-foreground">{lead.kavling_name}</p>
                    <p className="text-xs text-muted-foreground">{lead.project_name}</p>
                  </td>
                  <td className="px-4 py-3"><StatusBadge status={lead.stage} size="sm" /></td>
                  <td className="px-4 py-3 text-sm text-muted-foreground">{lead.source}</td>
                  <td className="px-4 py-3 text-sm text-foreground">{lead.budget || '—'}</td>
                  <td className="px-4 py-3 text-xs text-muted-foreground whitespace-nowrap">{formatDate(lead.created_at)}</td>
                  <td className="px-4 py-3">
                    <button
                      onClick={() => setSelectedLead(lead)}
                      className="text-xs font-semibold text-primary hover:underline"
                    >
                      Detail
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Lead Detail Modal */}
      {selectedLead && (
        <Modal isOpen={!!selectedLead} onClose={() => setSelectedLead(null)} title={selectedLead.name}>
          <div className="flex flex-col gap-4">
            {/* Lead info */}
            <div className="grid grid-cols-2 gap-3">
              <div className="bg-muted rounded-xl p-3">
                <p className="text-xs text-muted-foreground mb-0.5">Properti</p>
                <p className="text-sm font-semibold text-foreground">{selectedLead.kavling_name}</p>
                <p className="text-xs text-muted-foreground">{selectedLead.project_name}</p>
              </div>
              <div className="bg-muted rounded-xl p-3">
                <p className="text-xs text-muted-foreground mb-0.5">Kontak</p>
                <p className="text-sm font-semibold text-foreground">{selectedLead.phone}</p>
                <p className="text-xs text-muted-foreground">{selectedLead.source}</p>
              </div>
              {selectedLead.budget && (
                <div className="bg-muted rounded-xl p-3">
                  <p className="text-xs text-muted-foreground mb-0.5">Budget</p>
                  <p className="text-sm font-semibold text-primary">{selectedLead.budget}</p>
                </div>
              )}
              <div className="bg-muted rounded-xl p-3">
                <p className="text-xs text-muted-foreground mb-0.5">Status</p>
                <StatusBadge status={selectedLead.stage} size="sm" />
              </div>
            </div>

            {/* Move stage */}
            <div>
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">Pindah Stage</p>
              <div className="flex flex-wrap gap-2">
                {stages.map((s) => (
                  <button
                    key={`move-${s}`}
                    onClick={() => { updateLeadStatus(selectedLead.id, s); setSelectedLead((prev) => prev ? { ...prev, stage: s } : prev); }}
                    className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${
                      selectedLead.stage === s
                        ? 'bg-primary text-primary-foreground'
                        : 'bg-muted text-foreground hover:bg-primary/10 hover:text-primary'
                    }`}
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>

            {/* Timeline */}
            {selectedLead.timeline && selectedLead.timeline.length > 0 && (
              <div>
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">Timeline</p>
                <div className="flex flex-col gap-2 max-h-40 overflow-y-auto">
                  {selectedLead.timeline.map((t) => (
                    <div key={t.id} className="flex items-start gap-2">
                      <div className="w-1.5 h-1.5 rounded-full bg-primary mt-1.5 flex-shrink-0" />
                      <div>
                        <p className="text-xs font-semibold text-foreground">{t.action}</p>
                        {t.note && <p className="text-xs text-muted-foreground">{t.note}</p>}
                        <p className="text-xs text-muted-foreground">{formatDate(t.created_at)}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Add note */}
            <div>
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">Tambah Catatan</p>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={newNote}
                  onChange={(e) => setNewNote(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && addNote(selectedLead.id)}
                  placeholder="Tulis catatan follow-up..."
                  className="flex-1 px-3 py-2 bg-background border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
                />
                <button
                  onClick={() => addNote(selectedLead.id)}
                  disabled={isSavingNote || !newNote.trim()}
                  className="px-4 py-2 bg-primary text-primary-foreground rounded-lg text-sm font-semibold hover:bg-primary-dark transition-colors disabled:opacity-60"
                >
                  {isSavingNote ? <Icon name="ArrowPathIcon" size={14} className="animate-spin" /> : 'Simpan'}
                </button>
              </div>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}